import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { NFLPlayerMeta } from './NFLPlayerMeta';
import { shortPlayerName } from '@/lib/nflGameLine';
import { slotLabel } from '@/lib/nflRosterSlots';
import { benchLabel, pastWeekScoreText, rowCents, type WeekScoreRow } from '@/lib/nflWeekLineup';
import type { PastWeekData } from '@/hooks/usePastWeekLineup';

/**
 * My Team for a COMPLETED week: the lineup as it was scored, read-only.
 *
 * It replaces the editable Starters/Bench/IR tables only when the selected week's matchup is
 * completed. Those tables are TODAY's roster -- rendering them under "Week 3" is what showed a
 * manager players he benched that week. Moves, IR and drops act on the current roster, so they
 * do not belong on a record of a finished week.
 *
 * Two statements always made when they apply (see lib/nflWeekLineup.ts):
 *   "Slots reconstructed"  -- on every week carrying a derived slot.
 *   "Lineup not recorded"  -- a completed week with no score rows. Never the current roster.
 */
export function NFLPastWeekLineup({ data, week, onOpenPlayer }: {
  data: PastWeekData;
  week: number;
  onOpenPlayer?: (playerId: string) => void;
}) {
  const { lineup, info } = data;

  if (!lineup || lineup.status === 'not-recorded') {
    return (
      <Card className="bg-card border-border">
        <CardContent className="p-4">
          <p data-testid="lineup-not-recorded" className="text-sm text-muted-foreground">
            Lineup not recorded for week {week}.
          </p>
          <p className="mt-1 text-[11px] text-muted-foreground">
            The week was scored, but no per-player record of it exists, so its lineup cannot be shown.
          </p>
        </CardContent>
      </Card>
    );
  }

  const row = (r: WeekScoreRow, label: string, slotKey: string, muted = false) => {
    const p = info.get(r.id);
    return (
      <div key={r.id} data-past-row={slotKey} className="flex items-center gap-2 px-3 py-2 border-b border-border/30 last:border-0">
        <span className="w-10 shrink-0 text-[10px] font-bold text-muted-foreground">{label}</span>
        <div className="flex-1 min-w-0">
          <NFLPlayerMeta
            fullName={p?.full_name ?? 'Unknown'}
            shortName={shortPlayerName(p?.full_name ?? 'Unknown')}
            position={p?.position}
            teamAlias={p?.teamAlias}
            byeWeek={p?.byeWeek}
            injuryStatus={p?.injury_status}
            status={p?.status}
            isBye={p?.isBye}
            game={p?.game ?? null}
            onClick={onOpenPlayer ? () => onOpenPlayer(r.player_id) : undefined}
          />
        </div>
        <span className={cn('w-16 shrink-0 text-right text-sm font-bold tabular-nums', muted ? 'text-muted-foreground' : 'text-foreground')}>
          {pastWeekScoreText(rowCents(r))}
        </span>
      </div>
    );
  };

  return (
    <div className="space-y-3" data-testid="past-week-lineup">
      {lineup.hasDerived && (
        <p data-testid="slots-reconstructed" className="px-1 text-[11px] text-amber-400/90 leading-snug">
          Slots reconstructed: week {week} was scored before lineup slots were recorded. Who started
          is the record; which slot each starter filled is reconstructed.
        </p>
      )}

      <Card className="bg-card border-border">
        <CardContent className="p-0">
          <div className="flex items-center justify-between px-3 py-2 border-b border-border/40">
            <span className="text-sm font-semibold">Starters · week {week}</span>
            <span className="text-[10px] text-muted-foreground">as scored</span>
          </div>
          {lineup.slots.map(({ slot, row: r }) => r ? row(r, slotLabel(slot), slot) : (
            <div key={slot} className="flex items-center gap-2 px-3 py-2 border-b border-border/30">
              <span className="w-10 shrink-0 text-[10px] font-bold text-muted-foreground">{slotLabel(slot)}</span>
              <span className="text-xs italic text-muted-foreground">Empty</span>
            </div>
          ))}
          {lineup.alsoCounted.length > 0 && (
            <div data-testid="also-counted" className="border-t border-border">
              <p className="px-3 pt-2 text-[10px] uppercase tracking-wider text-muted-foreground">Also counted</p>
              {lineup.alsoCounted.map((r) => row(r, '+', 'ALSO', true))}
            </div>
          )}
        </CardContent>
      </Card>

      {lineup.bench.length > 0 && (
        <Card className="bg-card border-border">
          <CardContent className="p-0">
            <div className="px-3 py-2 border-b border-border/40 text-sm font-semibold">Bench · week {week}</div>
            {lineup.bench.map((r) => row(r, slotLabel(benchLabel(r)), benchLabel(r), true))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default NFLPastWeekLineup;
